import { t } from 'i18next';
import { EyeIcon, EyeOffIcon } from 'icons';
import { Tooltip } from 'primereact/tooltip';
import React, { useState } from 'react';

/* eslint-disable no-unused-vars */
type InputMode =
  | 'none'
  | 'text'
  | 'decimal'
  | 'numeric'
  | 'tel'
  | 'search'
  | 'email'
  | 'url';

interface InputFieldProps {
  label?: any;
  name?: string;
  type?: string;
  value: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: any;
  style?: any;
  fullWidth?: boolean;
  labelSize?: number;
  labelWidth?: number;
  inputHeight?: number;
  hideBorder?: boolean;
  disable?: boolean;
  error?: string;
  dropdown?: {
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    options: { label: string; value: string }[];
  };
  sentDateTooltip?: boolean;
  text?: any;
  multiple?: any;
  max?: string;
  min?: string;
  step?: string;
  maxLength?: number;
  inputMode?: InputMode;
}

/* Phone keyboards: give each field the keys it actually needs. */
const KEYBOARD_BY_TYPE: Record<string, InputMode> = {
  number: 'decimal',
  email: 'email',
  tel: 'tel',
  url: 'url',
  search: 'search',
};

const LOWERCASE_TYPES = ['email', 'url', 'password'];

export const InputField = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  placeholder,
  fullWidth,
  labelSize,
  labelWidth,
  inputHeight,
  hideBorder,
  disable,
  error,
  dropdown,
  text,
  sentDateTooltip,
  multiple,
  max,
  min,
  step,
  maxLength,
  inputMode,
}: InputFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const today = new Date();
  // Use local time formatting to avoid UTC conversion issues (e.g., UTC+2)
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const computedMaxDate =
    type === 'date'
      ? max || lastDayOfMonth.toLocaleDateString('en-CA')
      : undefined;

  const errorId = error && name ? `${name}-error` : undefined;
  const heightStyle = inputHeight
    ? { height: `${inputHeight}px`, maxHeight: `${inputHeight}px` }
    : undefined;

  return (
    <label
      className={`flex flex-col relative text-[#4F5057] ${
        fullWidth ? 'w-full' : ''
      } ${labelWidth ? '' : 'font-[400]'}`}
      style={{
        fontSize: labelSize ? `${labelSize}px` : '14px',
        fontWeight: labelWidth || undefined,
      }}
    >
      {!sentDateTooltip && label}
      {sentDateTooltip && (
        <div>
          {label}
          <Tooltip target=".sent-date-info" />
          <i
            className="sent-date-info pi pi-info-circle p-text-secondary p-overlay-badge ml-1"
            data-pr-tooltip={t('createNewInvoice.sentDateInfo') || ''}
            data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"
            style={{ fontSize: '1rem', cursor: 'pointer' }}
          ></i>
        </div>
      )}
      <div className="flex items-center relative">
        <input
          type={type === 'password' && showPassword ? 'text' : type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disable}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          inputMode={inputMode || KEYBOARD_BY_TYPE[type]}
          autoCapitalize={LOWERCASE_TYPES.includes(type) ? 'none' : undefined}
          autoCorrect={LOWERCASE_TYPES.includes(type) ? 'off' : undefined}
          spellCheck={LOWERCASE_TYPES.includes(type) ? false : undefined}
          enterKeyHint={type === 'search' ? 'search' : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          className={`placeholder-[#ABB4BF] placeholder:font-normal text-[16px] placeholder:text-[16px] rounded-[5px] px-[16px] py-[12px] flex-grow w-[100%]
            ${hideBorder ? 'border-none' : 'border border-[#D5D9DF]'}
            ${inputHeight ? '' : 'max-h-[48px] h-[48px] md:max-h-[45px] md:h-[45px]'}
            ${isFocused || !value ? 'bg-white' : 'bg-[#F4F4F4]'}
            ${type === 'date' ? 'appearance-none' : ''} ${
              dropdown || text ? 'rounded-r-none border-r-0' : ''
            }
     	 	${error ? 'border-red-500' : ''}
            ${disable ? 'opacity-60' : ''}`}
          style={heightStyle}
          multiple={multiple}
          max={computedMaxDate}
          min={min}
          step={step}
          maxLength={maxLength}
        />
        {dropdown && (
          <select
            name={dropdown.name}
            value={dropdown.value}
            onChange={dropdown.onChange}
            disabled={disable}
            aria-label={dropdown.name}
            className={`h-[48px] md:h-[45px] px-[4px] pr-[20px] text-[16px] border border-[#D5D9DF] border-l-0 rounded-r-[5px] w-[68px] shrink-0
              ${isFocused || !value ? 'bg-white' : 'bg-[#F4F4F4]'}
              ${disable ? 'opacity-60' : ''}`}
          >
            {dropdown.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
        {text && (
          <div
            className={`flex px-4 items-center leading-4 h-[48px] md:h-[45px] text-[16px] border border-[#D5D9DF] border-l-1 rounded-r-[5px] shrink-0 whitespace-nowrap
              ${isFocused || !value ? 'bg-white' : 'bg-[#F4F4F4]'}`}
          >
            {text}
          </div>
        )}
        {type === 'password' && !dropdown && (
          <button
            type="button"
            aria-label={t('common.togglePassword') as string}
            className="absolute right-[8px] self-center h-11 w-11 flex items-center justify-center"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        )}
      </div>
      <style>
        {`
          select {
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%234F5057' d='M0 0h10L5 6z'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 8px center;
            background-size: 10px 6px; /* Smaller arrow */
            -webkit-appearance: none; /* Remove default arrow */
            -moz-appearance: none;    /* Remove default arrow */
            appearance: none;         /* Remove default arrow */
          }
          select::-ms-expand {
            display: none; /* Remove default arrow in IE */
          }
          /* Override autofill styles */
          input:-webkit-autofill,
          input:-webkit-autofill:hover,
          input:-webkit-autofill:focus,
          input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 30px #F4F4F4 inset !important; /* Force bg-[#F4F4F4] */
            -webkit-text-fill-color: #4F5057 !important; /* Match text color */
            transition: background-color 5000s ease-in-out 0s; /* Prevent fade */
          }
        `}
      </style>
      {type === 'date' && (
        <style>
          {`
            input[type="date"]::-webkit-calendar-picker-indicator {
              color: #757575;
              opacity: 0.7;
              cursor: pointer;
            }
            input[type="date"]::-webkit-inner-spin-button,
            input[type="date"]::-webkit-clear-button {
              display: none;
            }
            input[type="date"] {
              line-height: normal;
            }
          `}
        </style>
      )}
      {maxLength && (
        <div className="text-right text-[12px] text-[#4f5057] mt-1 tnum">
          {value.length}/{maxLength}
        </div>
      )}
      {error && (
        <div id={errorId} className="text-red-500 text-sm mt-1">
          {error}
        </div>
      )}
    </label>
  );
};
